import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';

import { LecturePageContent } from '@/components/ui/features/library/LecturePageContent/LecturePageContent';
import { LectureSearchRedirectInput } from '@/components/ui/features/library/LectureSearchRedirectInput/LectureSearchRedirectInput';
import { LibraryHeader } from '@/components/ui/features/library/LibraryHeader/LibraryHeader';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { accessFrom } from '@/constants/custom-request-headers';
import { AccessFrom } from '@/enums/AccessFrom';
import { LectureSort } from '@/enums/LectureSort';
import { fetchLectureAccessPermissionsSideload } from '@/queries/lecture-access-permissions';
import { queryLecturesByQueryInfinite } from '@/queries/thin-lectures';
import { queryThinSubjectsInfinite } from '@/queries/thin-subjects';
import { ThinSubject } from '@/resources/ThinSubject';
import { generateStore } from '@/utils/generateStore';
import { Hydrate } from '@/utils/swr/Hydrate';

const NewLibraryPage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  fallback,
  accessFromLocation,
}) => {
  return (
    <Hydrate fallback={fallback}>
      <Layout accessFrom={accessFromLocation} navigationVariant="default">
        <LibraryHeader />
        <LectureSearchRedirectInput />
        <LecturePageContent />
      </Layout>
    </Hydrate>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  try {
    const response = await store.fetchResourcesInfinite(
      queryThinSubjectsInfinite({
        filters: {
          scope: 'library_featured',
        },
        sort: 'position',
        pageSize: 4,
      })
    );

    if (response) {
      const subjects = response.data as Array<ThinSubject>;

      const subjectPromises = subjects.map((subject) => {
        return store.fetchResourcesInfinite(
          queryLecturesByQueryInfinite({
            filters: {
              scope: 'published',
              library: 'true',
              subject_subtree_of_id: subject?.id,
            },
            include: ['sorted_profiles'],
            sort: `-${LectureSort.HeldAt}`,
            pageSize: 16,
          }),
          { sideload: fetchLectureAccessPermissionsSideload(store) }
        );
      });

      await Promise.all(subjectPromises);
    }
  } catch (error) {
    return {
      props: {
        fallback: store.fallback,
        accessFromLocation: context.req.headers[accessFrom] || AccessFrom.Core,
      },
    };
  }

  return {
    props: {
      fallback: store.fallback,
      accessFromLocation: context.req.headers[accessFrom] || AccessFrom.Core,
    },
  };
};
export default NewLibraryPage;
